import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { OrderService } from '@services';
import { ShippingDatesUpdate } from '@interfaces';
import { DatePipe, formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { BusinessUnitId } from '@enums';
import {
  DATE_FORMATS,
  LOCALE,
  TIME_SUFFIX,
  UI_TEXT,
} from './business-line-shipping-date.constants';

@Component({
  selector: 'unicon-business-line-shipping-date',
  standalone: false,
  templateUrl: './business-line-shipping-date.component.html',
  styleUrl: './business-line-shipping-date.component.scss',
})
export class BusinessLineShippingDateComponent implements OnChanges {
  @Input() businessLineId!: number;
  @Input() businessLineName!: string;
  @Input() orderId!: string;
  @Input() currentStartDateTime?: string;
  @Input() currentEndDateTime?: string;
  @Input() readonly: boolean = false;

  @Output() openGrid = new EventEmitter<{
    date: string;
    orderId: string;
    businessLineId: number;
    businessLineName: string;
  }>();

  public isLoading: boolean = false;
  public showCalendar: boolean = false;
  public selectedDate: Date | null = null;
  public minDate: Date = new Date();

  constructor(
    private orderService: OrderService,
    private datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    if (this.currentStartDateTime) {
      this.selectedDate = new Date(this.currentStartDateTime);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['currentStartDateTime'] &&
      changes['currentStartDateTime'].currentValue
    ) {
      this.selectedDate = new Date(
        changes['currentStartDateTime'].currentValue,
      );
    }
  }

  public get formattedDate(): string {
    if (!this.currentStartDateTime) {
      return UI_TEXT.NO_DATE_ASSIGNED;
    }

    if (this.businessLineId === BusinessUnitId.CONCRETE) {
      return this.formatDateTimeForConcrete();
    }

    return formatDate(
      this.currentStartDateTime,
      DATE_FORMATS.DATE_WITH_DAY,
      LOCALE.SPANISH,
    );
  }

  private formatDateTimeForConcrete(): string {
    if (!this.currentStartDateTime) {
      return UI_TEXT.PLACEHOLDER;
    }

    const date = formatDate(
      this.currentStartDateTime,
      DATE_FORMATS.DATE_WITH_DAY,
      LOCALE.SPANISH,
    );
    const startTime = formatDate(
      this.currentStartDateTime,
      DATE_FORMATS.TIME_24H,
      LOCALE.SPANISH,
    );

    if (this.currentEndDateTime) {
      const endTime = formatDate(
        this.currentEndDateTime,
        DATE_FORMATS.TIME_24H,
        LOCALE.SPANISH,
      );
      return `${date} - ${startTime} a ${endTime}`;
    }

    return `${date} - ${startTime}`;
  }

  public onContainerClick(): void {
    if (this.readonly || this.isLoading) {
      return;
    }

    this.showCalendar = !this.showCalendar;
  }

  public onDateChange(date: Date | null): void {
    if (date) {
      this.selectedDate = date;
    }
  }

  public confirmDateSelection(): void {
    if (!this.selectedDate) {
      return;
    }

    if (this.businessLineId === BusinessUnitId.CONCRETE) {
      this.showCalendar = false;
      const formattedDate = this.datePipe.transform(
        this.selectedDate,
        DATE_FORMATS.DATE_DISPLAY,
      );
      const dayName = new Intl.DateTimeFormat(LOCALE.SPANISH_ES, {
        weekday: 'long',
      }).format(this.selectedDate);
      const displayDate = `${dayName.charAt(0).toUpperCase()}${dayName.slice(1)}, ${formattedDate}`;

      this.openGrid.emit({
        date: displayDate,
        orderId: this.orderId,
        businessLineId: this.businessLineId,
        businessLineName: this.businessLineName,
      });

      return;
    }

    const dateStr = formatDate(
      this.selectedDate,
      DATE_FORMATS.DATE_ISO,
      LOCALE.ENGLISH,
    );
    const startDateTime = `${dateStr}${TIME_SUFFIX.DAY_START}`;
    const endDateTime = `${dateStr}${TIME_SUFFIX.DAY_END}`;
    this.showCalendar = false;
    this.updateDates(startDateTime, endDateTime);
  }

  public updateDates(startDateTime: string, endDateTime: string): void {
    if (!this.orderId) {
      return;
    }

    this.isLoading = true;

    const body: ShippingDatesUpdate = {
      businessLineId: this.businessLineId,
      shippingStartDateTime: startDateTime,
      shippingEndDateTime: endDateTime,
    };

    this.orderService.updateShippingDates(this.orderId, body).subscribe({
      next: () => {
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        console.error('Error updating shipping dates:', error);
      },
    });
  }
}
